// Post-action verification helpers for the dataset assistant. After every
// write we re-fetch the affected entity and compare it against the proposed
// change so the tool output reports the *actual* state, not just the dispatch
// result.

import type { SchemaFieldSummary } from "./types";

export interface VerificationReport {
    verified: boolean;
    appliedChanges: string[];
    unappliedChanges: string[];
}

function valuesEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return String(a) === String(b);
}

function resolveFieldKey(schemaFields: SchemaFieldSummary[], fieldName: string): string | undefined {
    const match = schemaFields.find((f) => (f?.name || "").toLowerCase() === (fieldName || "").toLowerCase());
    return match?.fieldId;
}

export function verifyUpdatedDataSet(
    proposed: { name?: string; description?: string },
    refreshed: { dataSetName?: string; dataSetDescription?: string } | null,
): VerificationReport {
    const applied: string[] = [];
    const unapplied: string[] = [];
    if (!refreshed) {
        return { verified: false, appliedChanges: [], unappliedChanges: ["data set could not be re-fetched"] };
    }
    if (proposed.name !== undefined) {
        if ((refreshed.dataSetName || "") === proposed.name) applied.push(`renamed to "${proposed.name}"`);
        else unapplied.push(`rename to "${proposed.name}" did not apply (server has "${refreshed.dataSetName || ""}")`);
    }
    if (proposed.description !== undefined) {
        if ((refreshed.dataSetDescription || "") === (proposed.description || "")) applied.push("description updated");
        else unapplied.push("description change did not apply");
    }
    return { verified: unapplied.length === 0, appliedChanges: applied, unappliedChanges: unapplied };
}

// Compares each proposed { fieldName: value } against the values stored on the
// re-fetched data object record (which is keyed by fieldId).
export function verifyDataObjectValues(
    proposedValuesByName: Record<string, unknown>,
    refreshed: Record<string, unknown> | null,
    schemaFields: SchemaFieldSummary[],
): VerificationReport {
    const applied: string[] = [];
    const unapplied: string[] = [];
    if (!refreshed) {
        return { verified: false, appliedChanges: [], unappliedChanges: ["data object could not be re-fetched"] };
    }
    for (const [fieldName, proposedValue] of Object.entries(proposedValuesByName)) {
        const fieldId = resolveFieldKey(schemaFields, fieldName);
        if (!fieldId) {
            unapplied.push(`field "${fieldName}" is not part of this schema`);
            continue;
        }
        const actual = refreshed[fieldId];
        if (valuesEqual(actual, proposedValue)) {
            applied.push(`${fieldName} = ${String(proposedValue)}`);
        } else {
            unapplied.push(`${fieldName} (expected ${String(proposedValue)}, got ${actual === undefined ? "no value" : String(actual)})`);
        }
    }
    return { verified: unapplied.length === 0, appliedChanges: applied, unappliedChanges: unapplied };
}
